import { ReactElement, FC, ChangeEvent } from "react";
import { LooseObject } from "../interfaces/LooseObject";
import {
  Box,
  Button,
  Container,
  FormControl,
  TextField,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import SnackBar from "./SnackBar";
import LoadingOverlay from "react-loading-overlay-ts";
import { AuthService } from "../services/auth.service";
import { StringUtils } from "../utils/string.utils";

const ForgotPassword: FC<LooseObject> = (props: any): ReactElement => {
  const [loading, setLoading] = React.useState(false);

  const [snackBarMessage, setSnackBarMessage] = React.useState("");
  const [snackBarOpen, setSnackBarOpen] = React.useState(false);
  const [snackBarType, setSnackBarType] = React.useState("success");
  const handleSnackBarClose = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSnackBarOpen(false);
  };

  const [isSent, setisSent] = React.useState(false);

  const [userData, setUserData] = React.useState({name:''});

  const [formData, setFormData] = React.useState({ 
    email: '',
    password: '',
    confirmPassword:'',
    otp: '',
  });

  // State to manage validation errors
  const [errors, setErrors] = React.useState({ 
    email: '',
    password: '',
    confirmPassword:'',
    otp: '',
  });

  // Function to handle form input changes
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target; 
    // Update form data
    setFormData({
      ...formData,
      [name]: value,
    });

    // Validate the field and update errors
    validateField(name, value);
  };

  // Returns the error message for a single field (empty string when valid).
  // Kept as a pure function (rather than reading straight from `errors` state)
  // so callers that need the up-to-date validity of *all* fields at once
  // (e.g. on submit) aren't stuck reading last render's stale state.
  const getFieldError = (fieldName: string, value: string): string => {
    switch (fieldName) {
      case 'email':
        if (value === '') return 'Email is required.';
        return StringUtils.isValidEmail(value) ? '' : 'Please enter a valid email address.';
      case 'password':
        return StringUtils.validatePassword(value, formData.email);
      case 'confirmPassword':
        if (value === '') return 'Confirm Password is required.';
        return value === formData.password ? '' : 'Confirm password do not match.';
      case 'otp':
        if (!isSent) return '';
        return value === '' ? 'OTP is required.' : '';
      default:
        return '';
    }
  };

  // Function to validate a specific field (updates the on-screen error as the user types)
  const validateField = (fieldName: string, value: string) => {
    const error = getFieldError(fieldName, value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: error,
    }));
  };


  const authService = new AuthService();

  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      handleForgotPassword();
    }
  };
  const resetUserState = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      otp: '',
    });
    setErrors({
      email: '',
      password: '',
      confirmPassword: '',
      otp: '',
    });
  };

  const handleStartOver = () => {
    resetUserState();
    setisSent(false);
  };

  const handleForgotPassword = () => {
    if (loading) return; // guard against double-submit while a request is in flight

    // Compute validity synchronously rather than reading the (still-stale,
    // pre-update) `errors` state right after calling validateField -- doing
    // that let a blank form pass validation on the very first click.
    const newErrors = {
      email: getFieldError("email", formData.email),
      password: getFieldError("password", formData.password),
      confirmPassword: getFieldError("confirmPassword", formData.confirmPassword),
      otp: getFieldError("otp", formData.otp),
    };
    setErrors(newErrors);

    if (!Object.values(newErrors).every((error) => error === '')) {
      return;
    }

    setLoading(true);

    if (!isSent) {
      authService
        .forgotPassword({ email: formData.email })
        .then((result: any) => {
          if (result) {
            setUserData({ name: result.name });
            setisSent(true);
            setSnackBarMessage("OTP sent on your register email.");
            setSnackBarType("success");
            setSnackBarOpen(true);
          } else {
            setSnackBarMessage("Failed to send OTP. Please try again.");
            setSnackBarType("error");
            setSnackBarOpen(true);
          }
          setLoading(false);
        })
        .catch((err: any) => {
          setSnackBarMessage(StringUtils.toErrorMessage(err));
          setSnackBarType("error");
          setSnackBarOpen(true);
          setLoading(false);
        });
    } else {
      authService
        .changePassword({ name: userData.name, email: formData.email, password: formData.password, otp: formData.otp })
        .then((result: any) => {
          if (result) {
            resetUserState();
            setisSent(false);
            setSnackBarMessage("Password has been changed Successfully");
            setSnackBarType("success");
            setSnackBarOpen(true);
          } else {
            setSnackBarMessage("Failed to change password. Please try again.");
            setSnackBarType("error");
            setSnackBarOpen(true);
          }
          setLoading(false);
        })
        .catch((err: any) => {
          setSnackBarMessage(StringUtils.toErrorMessage(err));
          setSnackBarType("error");
          setSnackBarOpen(true);
          setLoading(false);
        });
    }
  };
  const LogoProps: any = {
    height: "200",
    width: "400",
  };

  return (

    
    <React.Fragment>
      <Container
        sx={{
          maxWidth: "100%",
          width: "100%",
          padding: "15px",
          textAlign: "center",
        }}
      >
        <LoadingOverlay active={loading} spinner className="hsc-loader">
          <Box
            sx={{
              padding: "25px 50px",
              textAlign: "center",
              background: "#f8f6f6",
              width: "100%",
              maxWidth: 500,
              margin: "0 auto",
            }}
            onKeyPress={handleKeyPress}
          >
            <SnackBar
              id="snackbar-signup"
              message={snackBarMessage}
              type={snackBarType}
              open={snackBarOpen}
              close={handleSnackBarClose}
            />
            <div>
              <Logo {...LogoProps} />
            </div>
         
            <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
              <TextField
                sx={{ marginBlock: "10px" }}
                required
                id="outlined-email"
                label="Email"
                placeholder="Email"
                size="small"
                name="email"
                disabled={isSent} 
                value={formData.email}
                onChange={handleInputChange}
                error={errors.email!=""}
                helperText={errors.email}
              />
            </FormControl>
            <FormControl size="small" fullWidth sx={{ marginBlock: "5px"  }}>
              <TextField
                sx={{ marginBlock: "10px" }}
                required
                id="outlined-password"
                label="Password"
                placeholder="Password"
                size="small"
                type="password"
                name="password"
                disabled={isSent} 
                value={formData.password}
                onChange={handleInputChange}
                error={errors.password!=""}
                helperText={errors.password}
              />
            </FormControl>
            <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
              <TextField
                sx={{ marginBlock: "10px" }}
                required
                id="outlined-confirm-password"
                label="Confirm Password"
                placeholder="Confirm Password"
                size="small"
                type="password"
                name="confirmPassword"
                disabled={isSent} 
                value={formData.confirmPassword}
                onChange={handleInputChange}
                error={errors.confirmPassword!=""}
                helperText={errors.confirmPassword}
              />
            </FormControl> 
            <FormControl size="small" fullWidth sx={{ marginBlock: "5px",display: isSent ? '' : 'none' }}>
              <TextField
                sx={{ marginBlock: "10px" }}
                required
                id="outlined-otp"
                label="OTP"
                placeholder="OTP"
                size="small"
                name="otp"
                disabled={!isSent} 
                value={formData.otp}
                onChange={handleInputChange}
                error={errors.otp!=""}
                helperText={errors.otp}
              />
               </FormControl>
            <FormControl
              size="small"
              sx={{ marginBlock: "15px", padding: "25px" }}
            >
              <Button
                id="login-submit"
                variant="contained"
                onClick={handleForgotPassword}
                disabled={loading}
                size="large"
                color="primary"
                disableRipple={false}
                sx={{ padding: "5px 18px", bgcolor: "#c72825" }}
              >
                Submit
              </Button>
              {isSent && (
                <Button
                  id="start-over"
                  variant="text"
                  onClick={handleStartOver}
                  disabled={loading}
                  size="small"
                  sx={{ marginTop: "8px" }}
                >
                  Start Over
                </Button>
              )}
              <Box sx={{ marginTop: "12px" }}>
                <Link to="/">Back to Login</Link>
              </Box>
            </FormControl>
          </Box>
        </LoadingOverlay>
      </Container>
    </React.Fragment>
  );
};

export default ForgotPassword;
